import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCog } from "lucide-react";

export default function AdminProfileDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    current_password: "",
    new_password: "",
    confirm: "",
  });
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const load = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setForm((f) => ({ ...f, name: data?.profile?.name || "" }));
    } catch (e) { /* ignore */ }
  };

  React.useEffect(() => { if (open) load(); }, [open]);

  const save = async (e) => {
    e.preventDefault();
    if (form.new_password && form.new_password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (form.new_password && form.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      const body = { name: form.name };
      if (form.new_password) {
        body.current_password = form.current_password;
        body.new_password = form.new_password;
      }
      await api.patch("/staff/me", body);
      toast.success("Profile updated");
      setOpen(false);
      setForm({ name: form.name, current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button data-testid="admin-profile-btn"
                  className="h-9 w-9 rounded-full grid place-items-center"
                  aria-label="Edit profile"
                  style={{ background: "var(--surface-2)", border: "1px solid var(--border)", color: "var(--text)" }}>
            <UserCog className="h-4 w-4" />
          </button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="admin-profile-dialog">
        <DialogHeader><DialogTitle>Profile & password</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="ap-name">Display name</Label>
            <Input id="ap-name" data-testid="ap-name"
                   value={form.name} onChange={(e) => upd({ name: e.target.value })} />
          </div>
          <div className="pt-2 border-t" style={{ borderColor: "var(--border)" }}>
            <div className="label-caps mb-3 mt-2">Change password (optional)</div>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="ap-cur">Current password</Label>
                <Input id="ap-cur" data-testid="ap-cur" type="password" autoComplete="current-password"
                       value={form.current_password}
                       onChange={(e) => upd({ current_password: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ap-new">New password</Label>
                  <Input id="ap-new" data-testid="ap-new" type="password" autoComplete="new-password"
                         value={form.new_password}
                         onChange={(e) => upd({ new_password: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ap-conf">Confirm</Label>
                  <Input id="ap-conf" data-testid="ap-conf" type="password" autoComplete="new-password"
                         value={form.confirm}
                         onChange={(e) => upd({ confirm: e.target.value })} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="ap-save"
                    className="rounded-full text-white"
                    style={{ background: "var(--navy)" }}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
